import { useState } from 'react';
import { useRateJobMutation } from '../hooks/useJobs';
import { JobT } from '../utils/jobs/types';
import Button from './Button';
import Modal from './Modal';
import ModalContent from './ModalContent';

interface JobRatingProps {
  job: JobT;
  onRatingSubmitted?: () => void;
}

const JobRating = ({ job, onRatingSubmitted }: JobRatingProps) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { mutate: rateJob } = useRateJobMutation({
    onSuccess: () => {
      setIsSubmitting(false);
      setShowModal(false);
      setRating(0);
      setComment('');
      onRatingSubmitted?.();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Failed to submit rating:', error);
    },
  });

  const handleStarClick = (starRating: number) => {
    setRating(starRating);
    setShowModal(true);
  };

  const handleSubmitRating = () => {
    if (rating === 0) return;

    setIsSubmitting(true);
    rateJob({
      jobId: job.id,
      rating,
      comment: comment.trim() || undefined,
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setRating(0);
    setComment('');
  };

  const StarIcon = ({ filled, onMouseEnter, onMouseLeave, onClick }: {
    filled: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
  }) => (
    <button
      type="button"
      className={`w-6 h-6 transition-colors ${filled ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-300'
        }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <svg
        className="w-full h-full"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    </button>
  );

  if (job.rating) {
    return (
      <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
        <div className="text-center">
          <div className="text-xs text-green-400 mb-2">Job Rated</div>
          <div className="flex justify-center items-center space-x-1 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon key={star} filled={star <= job.rating!} />
            ))}
            <span className="text-sm text-green-300 ml-2">({job.rating}/5)</span>
          </div>
          {job.ratingComment && (
            <div className="text-xs text-green-200 italic mt-2">
              "{job.ratingComment}"
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
        <div className="text-center">
          <div className="text-xs text-purple-400 mb-3">Rate this job</div>

          <div className="flex justify-center items-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon
                key={star}
                filled={star <= hoveredRating}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                onClick={() => handleStarClick(star)}
              />
            ))}
          </div>
        </div>
      </div>

      <Modal open={showModal} onClose={handleCloseModal} className="!max-w-[400px]">
        <ModalContent
          heading={`${job.title}`}
          description={`You selected ${rating} star${rating !== 1 ? 's' : ''}`}
          className=""
        >
          <div className="space-y-4">
            <div className="flex justify-center items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon
                  key={star}
                  filled={star <= rating}
                />
              ))}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional comment (max 200 characters)"
              maxLength={200}
              className="w-full p-3 text-sm bg-gray-800 border border-gray-600 rounded-lg text-neutral-200 placeholder-gray-400 resize-none h-20"
              rows={3}
            />

            <div className="flex gap-3">
              {/* @ts-ignore */}
              <Button
                onClick={handleCloseModal}
                theme="secondary"
                className="flex-1"
              >
                Cancel
              </Button>

              {/* @ts-ignore */}
              <Button
                onClick={handleSubmitRating}
                theme="primary"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Rating'}
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default JobRating;
